import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Trash2 } from "lucide-react";
import {
    getAdminToken,
    clearAdminToken,
    adminStats,
    adminList,
    adminUpdateStatus,
    adminDelete,
} from "../lib/api";

const TABS = [
    { key: "quotes", label: "Quotes", statuses: ["new", "contacted", "converted", "archived"] },
    { key: "meetings", label: "Meetings", statuses: ["new", "confirmed", "done", "cancelled"] },
    { key: "contacts", label: "Contacts", statuses: ["new", "read", "archived"] },
];

const STATUS_TONE = {
    new: "bg-[#1A1A1A] text-[#FAF9F6]",
    contacted: "bg-[#8C7C6D] text-[#FAF9F6]",
    converted: "bg-[#2E7D32] text-white",
    archived: "bg-[#E0DDD5] text-[#4A4643]",
    confirmed: "bg-[#8C7C6D] text-[#FAF9F6]",
    done: "bg-[#2E7D32] text-white",
    cancelled: "bg-[#B23A3A] text-white",
    read: "bg-[#8C7C6D] text-[#FAF9F6]",
};

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [tab, setTab] = useState("quotes");
    const [stats, setStats] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!getAdminToken()) navigate("/admin/login", { replace: true });
    }, [navigate]);

    const refresh = useCallback(async () => {
        if (!getAdminToken()) return;
        setLoading(true);
        try {
            const [s, list] = await Promise.all([adminStats(), adminList(tab)]);
            setStats(s);
            setRows(list);
        } catch (err) {
            if (err?.response?.status === 401) {
                clearAdminToken();
                navigate("/admin/login", { replace: true });
            } else {
                toast.error("Could not load data.");
            }
        } finally {
            setLoading(false);
        }
    }, [tab, navigate]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const logout = () => {
        clearAdminToken();
        navigate("/admin/login", { replace: true });
    };

    const updateStatus = async (id, status) => {
        try {
            await adminUpdateStatus(tab, id, status);
            setRows((r) => r.map((x) => (x.id === id ? { ...x, status } : x)));
            toast.success("Status updated.");
        } catch {
            toast.error("Update failed.");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this record permanently?")) return;
        try {
            await adminDelete(tab, id);
            setRows((r) => r.filter((x) => x.id !== id));
            toast.success("Deleted.");
        } catch {
            toast.error("Delete failed.");
        }
    };

    const currentTab = TABS.find((t) => t.key === tab);

    return (
        <div className="min-h-screen bg-[#FAF9F6] text-[#1A1A1A] font-sans">
            <header className="border-b border-[#1A1A1A]/10 bg-white sticky top-0 z-30">
                <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
                    <div>
                        <div className="overline text-[#8C7C6D]">Design Desire · Studio Admin</div>
                        <h1 className="font-serif text-2xl mt-1 tracking-tight">Dashboard</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={refresh}
                            data-testid="admin-refresh"
                            className="px-4 py-2 border border-[#1A1A1A]/20 text-xs uppercase tracking-[0.15em] hover:bg-[#1A1A1A] hover:text-[#FAF9F6] transition-colors inline-flex items-center gap-2"
                        >
                            <RefreshCw size={14} strokeWidth={1.6} />
                            Refresh
                        </button>
                        <a
                            href="/"
                            className="px-4 py-2 border border-[#1A1A1A]/20 text-xs uppercase tracking-[0.15em] hover:bg-[#1A1A1A] hover:text-[#FAF9F6] transition-colors"
                            data-testid="admin-view-site"
                        >
                            View site
                        </a>
                        <button
                            onClick={logout}
                            data-testid="admin-logout"
                            className="px-4 py-2 bg-[#1A1A1A] text-[#FAF9F6] text-xs uppercase tracking-[0.15em] hover:bg-[#2A2421] inline-flex items-center gap-2"
                        >
                            <LogOut size={14} strokeWidth={1.6} />
                            Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-[1500px] mx-auto px-6 md:px-10 py-10">
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                    <StatCard
                        label="Quote requests"
                        total={stats?.quotes.total}
                        accent={stats?.quotes.new}
                        accentLabel="new"
                        testid="stat-quotes"
                    />
                    <StatCard
                        label="Meetings booked"
                        total={stats?.meetings.total}
                        accent={stats?.meetings.upcoming}
                        accentLabel="upcoming"
                        testid="stat-meetings"
                    />
                    <StatCard
                        label="Contact messages"
                        total={stats?.contacts.total}
                        accent={stats?.contacts.new}
                        accentLabel="new"
                        testid="stat-contacts"
                    />
                </div>

                {/* Tabs */}
                <div className="flex gap-1 border-b border-[#1A1A1A]/15 mb-8">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`admin-tab-${t.key}`}
                            className={`px-6 py-3 overline transition-colors border-b-2 ${
                                tab === t.key
                                    ? "border-[#1A1A1A] text-[#1A1A1A]"
                                    : "border-transparent text-[#4A4643] hover:text-[#1A1A1A]"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* Table */}
                <div className="bg-white border border-[#1A1A1A]/10 overflow-hidden">
                    {loading ? (
                        <div className="p-12 text-center text-[#4A4643]">Loading...</div>
                    ) : rows.length === 0 ? (
                        <div className="p-12 text-center text-[#4A4643]">
                            <div className="font-serif text-2xl">No {tab} yet.</div>
                            <div className="text-sm mt-2">
                                New submissions will appear here.
                            </div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid={`admin-table-${tab}`}>
                                <thead>
                                    <tr className="border-b border-[#1A1A1A]/10 bg-[#F0EFEA]">
                                        <Th>Submitted</Th>
                                        <Th>Name</Th>
                                        <Th>Contact</Th>
                                        {tab === "quotes" && <Th>Project</Th>}
                                        {tab === "quotes" && <Th>Budget</Th>}
                                        {tab === "meetings" && <Th>Date & Time</Th>}
                                        {tab === "meetings" && <Th>Mode</Th>}
                                        {tab === "contacts" && <Th>Subject</Th>}
                                        <Th>Details</Th>
                                        <Th>Status</Th>
                                        <Th>Actions</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => (
                                        <Row
                                            key={row.id}
                                            row={row}
                                            tab={tab}
                                            statuses={currentTab.statuses}
                                            onStatus={(s) => updateStatus(row.id, s)}
                                            onDelete={() => remove(row.id)}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}

const StatCard = ({ label, total, accent, accentLabel, testid }) => (
    <div
        data-testid={testid}
        className="bg-white border border-[#1A1A1A]/10 p-6 hover:border-[#1A1A1A]/30 transition-colors"
    >
        <div className="overline text-[#8C7C6D]">{label}</div>
        <div className="mt-3 flex items-baseline gap-3">
            <div className="font-serif text-5xl font-light">{total ?? "—"}</div>
            {accent !== undefined && (
                <div className="text-xs text-[#4A4643]">
                    <span className="font-medium text-[#1A1A1A]">{accent}</span> {accentLabel}
                </div>
            )}
        </div>
    </div>
);

const Th = ({ children }) => (
    <th className="text-left px-4 py-3 overline text-[#4A4643] font-medium">{children}</th>
);

const Td = ({ children, className = "" }) => (
    <td className={`px-4 py-4 align-top text-[#1A1A1A] ${className}`}>{children}</td>
);

const Row = ({ row, tab, statuses, onStatus, onDelete }) => {
    const date = new Date(row.created_at);
    const fmt = `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
    })}`;

    return (
        <tr className="border-b border-[#1A1A1A]/10 hover:bg-[#FAF9F6]">
            <Td className="text-xs text-[#4A4643] whitespace-nowrap">{fmt}</Td>
            <Td className="font-medium">{row.name}</Td>
            <Td className="text-xs">
                <div>{row.email}</div>
                {row.phone && <div className="text-[#4A4643]">{row.phone}</div>}
            </Td>
            {tab === "quotes" && <Td className="text-xs">{row.project_type}</Td>}
            {tab === "quotes" && <Td className="text-xs">{row.budget}</Td>}
            {tab === "meetings" && (
                <Td className="text-xs whitespace-nowrap">
                    <div className="font-medium">{row.date}</div>
                    <div className="text-[#4A4643]">{row.time_slot}</div>
                </Td>
            )}
            {tab === "meetings" && <Td className="text-xs capitalize">{row.mode}</Td>}
            {tab === "contacts" && <Td className="text-xs">{row.subject || "—"}</Td>}
            <Td className="text-xs max-w-[280px]">
                <div className="text-[#4A4643] line-clamp-3 whitespace-normal">
                    {row.message || row.notes || row.location || "—"}
                </div>
            </Td>
            <Td>
                <select
                    value={row.status}
                    onChange={(e) => onStatus(e.target.value)}
                    data-testid={`status-${row.id}`}
                    className={`px-2 py-1 text-[10px] uppercase tracking-[0.12em] border-none cursor-pointer ${
                        STATUS_TONE[row.status] || "bg-[#E0DDD5] text-[#1A1A1A]"
                    }`}
                >
                    {statuses.map((s) => (
                        <option key={s} value={s}>
                            {s}
                        </option>
                    ))}
                </select>
            </Td>
            <Td>
                <button
                    onClick={onDelete}
                    data-testid={`delete-${row.id}`}
                    className="p-2 text-[#4A4643] hover:text-[#B23A3A] hover:bg-[#FAF9F6] transition-colors"
                    aria-label="Delete"
                    title="Delete"
                >
                    <Trash2 size={16} strokeWidth={1.6} />
                </button>
            </Td>
        </tr>
    );
};
